/**
 * AppShell — Shared layout shell for all Heyward pages.
 * Provides sidebar, mobile nav, dot grid, frosted-noise content area,
 * and exports all Zolplay-derived design primitives.
 *
 * Usage: <AppShell variant="default|transparent">{page content}</AppShell>
 *
 * variant="default"     — white content area with frosted-noise + grid lines (redesigned pages)
 * variant="transparent"  — no content styling, just sidebar + margin offset (dark-themed pages)
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Home, MapPin, FileText, PieChart, Menu, X as XIcon, Compass, LogOut, Copy, Check, Settings } from 'lucide-react';
import { colors, neutral, accent, sidebar as sidebarStyle, dotGrid, gradientCSS } from '../../config/designSystem';
import { useAuth } from '../auth/AuthWrapper';
import useUserEmail from '../../hooks/useUserEmail';
import { usePortfolioData } from '../../contexts/PortfolioDataContext';
import { lazyWithRetry } from '../../utils/lazyRetry';

// =============================================================================
// DESIGN TOKENS
// =============================================================================

export const ease = [0.16, 1, 0.3, 1];

/** Color bridge — single source of truth via designSystem.js */
export const c = {
  pageBg:    colors.bg.page,
  stone100:  colors.bg.sidebar,
  surface:   colors.bg.content,
  elevated:  colors.bg.elevated,                // ADR-017 §11.4 — subtle raised surface (#f5f5f5)
  text:      colors.text.primary,
  textStrong: colors.text.strong,               // ADR-017 §11.4 — bold body text (#171717)
  textMid:   colors.text.secondary,
  textMute:  colors.text.muted,
  textFaint: colors.text.faint,
  black:     colors.text.strong,
  border:    colors.border.default,
  borderF:   colors.border.subtle,
  ambientScrim: colors.overlay.ambient,
  modalScrim:   colors.overlay.modal,
  stone800:  colors.interactive.hover,
  rose500:   accent.rose[500],
  indigo500: accent.indigo[500],
  red400:    accent.red[400],
  red600:    accent.red[600],
  neutral300: neutral[300],

  // Financial semantic (matches CashFlowTab reference) — emerald-600 / red-600 / amber-600
  success:   accent.emerald[600],          // #059669
  danger:    accent.red[600],              // #dc2626
  warning:   accent.amber[600],            // #d97706 — matches existing component usage
  online:    accent.green[500],            // #22c55e — agent online indicator

  // Darker variants for text on tinted backgrounds (Iteration C audit H4 fix).
  // Use these for small caps category labels (10-12px) on rgba tints — the
  // standard 600 variants fail WCAG AA contrast at small sizes.
  successDark: accent.emerald[700],         // #047857 — text on green tints
  dangerDark:  accent.red[700],             // #b91c1c — text on red tints
  warningDark: accent.amber[700],           // #b45309 — text on amber tints
  rose700:     accent.rose[700],            // #be123c — text on rose tints
  indigo700:   accent.indigo[700],          // #4338ca — text on indigo tints

  // Background tints for sentiment surfaces
  successTint:      'rgba(5,150,105,0.08)',
  dangerTint:       'rgba(220,38,38,0.08)',
  warningTint:      'rgba(217,119,6,0.08)',     // amber-600 at 8%
  successTintLight: 'rgba(5,150,105,0.04)',
  dangerTintLight:  'rgba(220,38,38,0.04)',
  successTintFaint: 'rgba(5,150,105,0.06)',
  dangerTintFaint:  'rgba(220,38,38,0.06)',
  infoTintLight:    'rgba(99,102,241,0.04)',

  // Rose / indigo tints — "agent memory" surfaces (replaces off-palette purple)
  roseTint:         'rgba(244,63,94,0.08)',     // rose-500 at 8%
  roseTintFaint:    'rgba(244,63,94,0.04)',
  indigoTint:       'rgba(99,102,241,0.08)',    // indigo-500 at 8%

  // Info family — postcode hints, transient blue status surfaces
  info:             accent.blue[600],            // #2563eb
  infoTint:         'rgba(37,99,235,0.08)',
  infoBorder:       'rgba(37,99,235,0.15)',

  // Phase-chip palette (DealsListPage badge fills + dots).
  // Direct shade references — kept distinct from the success/danger/warning
  // semantic aliases so swapping the green family doesn't accidentally
  // reskin every settlement-state UI in the app.
  green500:         accent.green[500],            // #22c55e — solid badge fill
  green600:         accent.green[600],            // #16a34a — phase dot
  green700:         accent.green[700],            // #15803d — text on light/tint
  blue500:          accent.blue[500],             // #3b82f6 — managing-phase dot
  blue700:          accent.blue[700],             // #1d4ed8 — text on blue tint
  // Phase-chip soft tint backgrounds (matches Tailwind's *-100 shades).
  // Not yet in designSystem.js because they're presentation-layer only;
  // add to accent.<color>[100] there if more pages start needing them.
  chipTintGreen:    '#dcfce7',                    // green-100 — settled-badge bg
  chipTintBlue:     '#dbeafe',                    // blue-100 — managing-badge bg
  chipTintIndigo:   '#e0e7ff',                    // indigo-100 — pre-offer-badge bg

  // Sentiment borders (15% opacity — for outlines on tinted surfaces)
  successBorder: 'rgba(5,150,105,0.15)',
  dangerBorder:  'rgba(220,38,38,0.15)',
  warningBorder: 'rgba(217,119,6,0.15)',
  roseBorder:    'rgba(244,63,94,0.15)',
  indigoBorder:  'rgba(99,102,241,0.15)',

  // Pure white & alphas (for inverted text on dark stone-800 surfaces)
  white:        '#ffffff',
  whiteAlpha10: 'rgba(255,255,255,0.1)',
  whiteAlpha70: 'rgba(255,255,255,0.7)',
};

// =============================================================================
// PRIMITIVES
// =============================================================================

export function Cross({ className }) {
  return <svg width="5" height="5" viewBox="0 0 5 5" className={className} aria-hidden="true"><path d="M2 0h1v2h2v1h-2v2h-1v-2h-2v-1h2z" fill={c.textMute}/></svg>;
}

export function Caption({ children }) {
  return <span className="block text-[11px] font-mono tracking-widest select-none opacity-65 pl-2 pb-1" style={{ color: c.textMute }} aria-hidden="true">{children}</span>;
}

export function Accent({ children }) {
  return <span style={gradientCSS.primary()}>{children}</span>;
}

export function RedAccent({ children }) {
  return <span style={gradientCSS.red()}>{children}</span>;
}

/**
 * Marker — section heading with numeric prefix.
 *
 * Renders as an `<h2>` by default so the page has a real heading outline for
 * screen readers (the visual treatment via spans is independent of the
 * semantic role). Pass `as="h3"` etc. when nesting under another Marker.
 *
 * The `label` can be a string OR a React node (Iteration C uses a JSX label
 * for the Verdict heading); the heading still wraps cleanly.
 */
export function Marker({ n, label, right, as: Tag = 'h2' }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <Tag className="flex items-center gap-2.5 m-0 p-0 font-normal text-inherit">
        <span className="text-[12px] font-bold tabular-nums" style={{ color: c.rose500, fontFeatureSettings: "'tnum'" }}>[{n}]</span>
        <span className="text-[12px] font-bold uppercase" style={{ color: c.textMute, letterSpacing: '0.12em' }}>{label}</span>
      </Tag>
      {right}
    </div>
  );
}

export function Frame({ children, className = '' }) {
  return (
    <div className={`relative ${className}`} style={{ border: `1px solid ${c.border}` }}>
      <Cross className="absolute -top-[2px] -left-[2px] opacity-40" />
      <Cross className="absolute -top-[2px] -right-[2px] opacity-40" />
      <Cross className="absolute -bottom-[2px] -left-[2px] opacity-40" />
      <Cross className="absolute -bottom-[2px] -right-[2px] opacity-40" />
      {children}
    </div>
  );
}

export function FullRule() {
  return <div className="w-full h-px" style={{ background: c.border }} />;
}

// =============================================================================
// NAV DATA & AUTH
// =============================================================================

// Lazy imports to avoid AppShell circular deps with agent components.
// lazyWithRetry so a transient chunk-fetch failure retries instead of
// poisoning React.lazy for the rest of the session (see utils/lazyRetry.js).
const ActivityPulse  = lazyWithRetry(() => import('../agent/ActivityPulse'), 'ActivityPulse');
const AdvocateChip   = lazyWithRetry(() => import('../agent/AdvocateChip'), 'AdvocateChip');
const MobileAskButton = lazyWithRetry(() => import('../agent/MobileAskButton'), 'MobileAskButton');
// 04_USER plan + usage meter (2026-08-19) — lazy like its siblings.
const SidebarUsageMeter = lazyWithRetry(() => import('./SidebarUsageMeter'), 'SidebarUsageMeter');

const AGENT_STATUSES = [
  'Heyward online',
  'Scanning market data...',
  'Heyward online',
  'Monitoring your deals...',
  'Heyward online',
  'Checking flood maps...',
];


const ALL_LINKS = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/suburb-search', label: 'Suburbs', icon: MapPin },
  { to: '/prop-search', label: 'Research', icon: Search, tourId: 'nav-research' },
  { to: '/deals', label: 'Deals', icon: FileText, tourId: 'nav-deals' },
  { to: '/portfolio', label: 'Portfolio', icon: PieChart },
  { to: '/strategy', label: 'Strategy', icon: Compass, tourId: 'nav-strategy' },
];

function useCanAccess() {
  const { isAdmin, isDevelopment } = useAuth();
  const isProduction = import.meta.env.PROD;
  return isDevelopment || isAdmin;
}

function isActive(pathname, to) {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

// =============================================================================
// MOBILE NAV
// =============================================================================

function MobileNav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, signOut, isAuthenticated, isDevelopment } = useAuth();
  const { clearAll } = usePortfolioData();
  const canAccess = useCanAccess();
  const visibleLinks = ALL_LINKS.filter(l => !l.gated || canAccess);

  useEffect(() => setOpen(false), [pathname]);

  const handleSignOut = async () => {
    try {
      clearAll();
      localStorage.removeItem('heyward_onboarding_progress');
      localStorage.removeItem('heyward_strategy_progress');
      // In dev mode the DevAuthProvider's signOut is a no-op. Flip
      // AuthWrapper's runtime override so the next render routes through
      // Clerk and shows the SignIn page, letting you log in as your real
      // account. The 'use dev account' link in the sign-in footer
      // clears this flag to come back.
      if (isDevelopment) {
        localStorage.setItem('heyward_dev_force_clerk_auth', '1');
        window.location.reload();
        return;
      }
      await signOut();
    } catch (e) { /* ignore */ }
  };

  return (
    <header className="sticky lg:hidden top-0 z-50 app-mobile-header" style={{ background: `${c.pageBg}ee`, backdropFilter: 'blur(8px)', borderBottom: `1px solid ${c.border}` }}>
      <div className="flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex min-h-11 min-w-11 items-center">
          <span className="text-[22px]" style={{ color: c.text, fontFamily: "'Libre Caslon Text', Georgia, serif", fontWeight: 700, letterSpacing: '-0.014em', lineHeight: 1 }}>Heyward</span>
        </Link>
        <div className="flex items-center gap-1">
          {isAuthenticated && (
            <React.Suspense fallback={null}>
              <MobileAskButton />
            </React.Suspense>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-800 focus-visible:ring-offset-2"
            style={{ color: c.text }}
            aria-label="Toggle navigation"
            aria-expanded={open}
          >
            {open ? <XIcon size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden" style={{ borderTop: `1px solid ${c.border}` }}>
            <div className="px-4 py-3 space-y-1">
              {visibleLinks.map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to} className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[14px] font-medium"
                  style={{ color: isActive(pathname, to) ? c.black : c.textMute, background: isActive(pathname, to) ? c.borderF : 'transparent' }}>
                  <Icon size={16} aria-hidden="true" />{label}
                </Link>
              ))}
            </div>
            {/* 03_STATUS + 05_ADVOCATE — mirror the desktop sidebar's
                bottom sections in the mobile drawer so phone/tablet users
                see Agent activity + advocate counters that were previously
                desktop-only. The chips bring their own Caption + padding
                so we don't wrap them in a px-4 parent. */}
            {isAuthenticated && (
              <div style={{ borderTop: `1px solid ${c.border}` }}>
                <React.Suspense fallback={null}>
                  <ActivityPulse fallback={AGENT_STATUSES[0]}/>
                </React.Suspense>
                <React.Suspense fallback={null}>
                  <AdvocateChip/>
                </React.Suspense>
              </div>
            )}
            {isAuthenticated && (
              <React.Suspense fallback={null}>
                <SidebarUsageMeter />
              </React.Suspense>
            )}
            {isAuthenticated && (
              <div className="px-4 pb-3 pt-1" style={{ borderTop: `1px solid ${c.border}` }}>
                <Link to="/settings" className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[14px] font-medium"
                  style={{ color: isActive(pathname, '/settings') ? c.black : c.textMute, background: isActive(pathname, '/settings') ? c.borderF : 'transparent' }}>
                  <Settings size={16} aria-hidden="true" />Settings
                </Link>
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-[12px] font-medium truncate" style={{ color: c.textMute }}>
                    {user?.attributes?.given_name || user?.username || 'User'}
                    {isDevelopment && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-sm font-bold uppercase" style={{ background: 'rgba(41,37,36,0.08)', color: c.stone800 }}>Dev</span>}
                  </span>
                  <button onClick={handleSignOut} className="p-1.5 rounded-sm" style={{ color: c.textMute }} title="Sign Out" aria-label="Sign out">
                    <LogOut size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

// =============================================================================
// APP SHELL
// =============================================================================

export default function AppShell({ children, variant = 'default' }) {
  const { pathname } = useLocation();
  const { user, signOut, isAuthenticated, isDevelopment } = useAuth();
  const { emailAddress, loading: emailLoading } = useUserEmail();
  const canAccess = useCanAccess();
  const visibleLinks = ALL_LINKS.filter(l => !l.gated || canAccess);

  const [copied, setCopied] = useState(false);
  // The old `agentStatusIdx` setInterval cycle was dead — its only
  // consumer was the `fallback` prop on ActivityPulse, which is shown
  // only when no real activity exists. ActivityPulse handles its own
  // 5s rotation against real SSE events; the AGENT_STATUSES wheel
  // would only ever appear on cold-start and would never visibly
  // rotate (the page navigates / a real event lands within 6s).
  // Pass a single static fallback string instead.

  const { clearAll } = usePortfolioData();

  const handleSignOut = async () => {
    try {
      clearAll();
      localStorage.removeItem('heyward_onboarding_progress');
      localStorage.removeItem('heyward_strategy_progress');
      // In dev mode the DevAuthProvider's signOut is a no-op. Flip
      // AuthWrapper's runtime override so the next render routes through
      // Clerk and shows the SignIn page, letting you log in as your real
      // account. The 'use dev account' link in the sign-in footer
      // clears this flag to come back.
      if (isDevelopment) {
        localStorage.setItem('heyward_dev_force_clerk_auth', '1');
        window.location.reload();
        return;
      }
      await signOut();
    } catch (e) { /* ignore */ }
  };

  const handleCopyEmail = async () => {
    if (!emailAddress) return;
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.error('Failed to copy:', err); }
  };

  const isTransparent = variant === 'transparent';

  return (
    // overflow-x: clip (not hidden) — clips full-bleed decorations
    // identically but doesn't create a scroll container, so descendant
    // position:sticky (deal-file chapter strip, mobile summit) keeps
    // working against the viewport.
    <div className="min-h-screen" style={{ background: c.pageBg, color: c.text, overflowX: 'clip' }}>
      {/* Dot grid */}
      <div className="fixed inset-0 pointer-events-none" style={dotGrid.light} />

      {/* Skip navigation */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:text-[13px] focus:font-semibold focus:rounded-md" style={{ background: c.stone800, color: '#FFF' }}>
        Skip to main content
      </a>

      <MobileNav />

      {/* ============ SIDEBAR ============ */}
      <aside
        className="hidden lg:flex flex-col justify-between w-72 shrink-0 fixed left-0 top-0 h-screen overflow-hidden z-30"
        style={{
          backgroundColor: c.stone100,
          ...sidebarStyle.getPattern(),
          borderRight: `1px solid ${c.border}`,
        }}
      >
        <div className={`min-h-0 overflow-y-auto ${isDevelopment ? 'pt-14' : ''}`}>
          <Link to="/" className="group relative flex items-center justify-center px-5 py-5" style={{ borderBottom: `1px solid ${c.border}` }}>
            <Cross className="absolute top-[2px] left-[2px] opacity-30 group-hover:opacity-100 transition-opacity" />
            <Cross className="absolute top-[2px] right-[2px] opacity-30 group-hover:opacity-100 transition-opacity" />
            <Cross className="absolute bottom-[2px] left-[2px] opacity-30 group-hover:opacity-100 transition-opacity" />
            <Cross className="absolute bottom-[2px] right-[2px] opacity-30 group-hover:opacity-100 transition-opacity" />
            <span className="text-[26px]" style={{ color: c.text, fontFamily: "'Libre Caslon Text', Georgia, serif", fontWeight: 700, letterSpacing: '-0.014em', lineHeight: 1 }}>Heyward</span>
          </Link>

          <nav className="mt-6" aria-label="Main navigation">
            <Caption>02_NAVIGATION</Caption>
            <ul style={{ borderBottom: `1px solid ${c.border}` }}>
              {visibleLinks.map(({ to, label, icon: Icon, tourId }) => {
                const active = isActive(pathname, to);
                return (
                  <li key={to} style={{ borderTop: `1px solid ${c.border}` }}>
                    <Link to={to} {...(tourId ? { 'data-tour': tourId } : {})} className="relative flex items-center gap-3 px-5 py-4 text-[16px] font-medium tracking-tight transition-all duration-150 hover:bg-[rgba(0,0,0,0.02)]"
                      style={{ color: active ? c.black : c.textMute }}>
                      {active && <motion.span className="absolute inset-0 -z-10" style={{ background: c.surface, outline: `1px solid ${c.border}` }} layoutId="anav" />}
                      <Icon size={18} aria-hidden="true" />{label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className="pb-4">
          {/* 03_STATUS — ADR-017 §11.4: real ActivityPulse, not cosmetic
              cycling text. Cycles through the user's actual recent
              activity (audit_log + memory) on a 5s rotation.
              Section order matches the numeric labels: 01 → 02 → 03 →
              04 → 05 (was 01 → 02 → 04 → 03 → 05 pre-fix; visual audit
              flagged the inconsistency). */}
          <React.Suspense fallback={null}>
            <ActivityPulse fallback={AGENT_STATUSES[0]}/>
          </React.Suspense>

          {/* 04_USER */}
          {isAuthenticated && (
            <>
              <Caption>04_USER</Caption>
              <div style={{ borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}` }}>
                <div className="flex items-start justify-between px-5 py-3 gap-3">
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[12px] font-semibold uppercase truncate" style={{ color: c.textMute, letterSpacing: '0.04em' }}>
                        {user?.attributes?.given_name || user?.username || 'User'}
                      </span>
                      {isDevelopment && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold uppercase shrink-0" style={{ background: 'rgba(41,37,36,0.08)', color: c.stone800, letterSpacing: '0.04em' }}>
                          Dev
                        </span>
                      )}
                    </div>
                    {/* Heyward deal-tracking alias instead of the personal
                        sign-in email — keeps the user's real address off-screen
                        for demos/screen-shares and surfaces the product-relevant
                        forwarding address. We intentionally never render the
                        personal email here. */}
                    {emailAddress && !emailLoading && (
                      <button onClick={handleCopyEmail} className="flex items-center gap-1.5 mt-0.5 min-w-0 text-left" title="Click to copy your Heyward deal-tracking email">
                        <span className="text-[11px] font-mono truncate" style={{ color: c.textFaint }}>{emailAddress}</span>
                        {copied
                          ? <Check size={10} className="shrink-0" style={{ color: '#22C55E' }} />
                          : <Copy size={10} className="shrink-0" style={{ color: c.textFaint }} />
                        }
                      </button>
                    )}
                  </div>
                  <button onClick={handleSignOut} className="p-1.5 rounded-sm transition-colors duration-150 shrink-0" style={{ color: c.textMute }} title="Sign Out" aria-label="Sign out"
                    onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = c.textMute}>
                    <LogOut size={14} aria-hidden="true" />
                  </button>
                </div>
                {/* Plan + scarcest allowance, with the upgrade nudge only as
                    usage drains (SidebarUsageMeter). Sits between the
                    identity row and Settings: the member's standing, then
                    where to change it. */}
                <React.Suspense fallback={null}>
                  <SidebarUsageMeter />
                </React.Suspense>
                <Link to="/settings" className="flex items-center gap-2 w-full px-5 py-2.5 text-left transition-colors duration-150 hover:bg-[rgba(0,0,0,0.02)]"
                  style={{ color: isActive(pathname, '/settings') ? c.black : c.textMute, borderTop: `1px solid ${c.borderF}` }}>
                  <Settings size={13} aria-hidden="true" />
                  <span className="text-[12px] font-medium">Settings</span>
                </Link>
              </div>
            </>
          )}

          {/* 05_ADVOCATE — ADR-017 §11.4: count chip for unread rail items.
              Click opens the AdvocateRail. Shown only for authenticated users. */}
          {isAuthenticated && (
            <React.Suspense fallback={null}>
              <AdvocateChip/>
            </React.Suspense>
          )}
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      {/* lg:ml-80 reserves 320px on the left when the sidebar is shown
          (lg+ only — sidebar collapses to MobileNav hamburger below 1024).
          md:mr-12 reserves 48px on the right so page content doesn't slip
          behind the AdvocateRail's collapsed strip (48px, fixed right:0,
          z-index 70 — AdvocateRail.jsx:766-778). The strip is hidden below
          768px, hence the md: gate stays independent of the sidebar
          breakpoint. Without this, pages clip their right edge (date
          strip, section headings) at tablet widths. */}
      <div className="lg:ml-80 md:mr-12">
        {isTransparent ? (
          <main id="main-content" tabIndex={-1} className="relative min-h-screen outline-none">
            {children}
          </main>
        ) : (
          // overflowX clip (not hidden): identical horizontal containment for
          // full-bleed decorations, but no scroll container is created, so
          // descendant position:sticky (deal-file strip/summit) keeps working.
          <main id="main-content" tabIndex={-1} className="frosted-noise relative min-h-screen border-x outline-none" style={{ background: c.surface, borderColor: c.border, overflowX: 'clip' }}>
            <div className="absolute left-7 lg:left-9 top-0 bottom-0 w-px hidden lg:block" style={{ background: 'rgba(0,0,0,0.07)' }} />
            <div className="absolute right-7 lg:right-9 top-0 bottom-0 w-px hidden lg:block" style={{ background: 'rgba(0,0,0,0.07)' }} />

            <div className="px-4 md:px-8 lg:px-10 pt-10 md:pt-16 lg:pt-20 pb-8">
              {children}
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
